import { FormStepType } from "../types";
import { gender } from "../options/gender";
import getYesNoOptions from "../options/yes_no_options";

export const getParentsAndSpouseStep = ({ id }: { id: number }): FormStepType => {
  return {
    id: id,
    name: "parent-spouse-information",
    title: `${id}. Parents and Spouse`,
    subLabel:
      "Provide the details of both your parents if possible. If you do not know the details of a parent, click ‘unknown’.",
    groups: [
      {
        name: "relative1Status",
        title: "Parent 1 details",
        fields: [
          {
            component: "Check",
            name: "relative1Unknown",
            label: "Unknown",
            type: "checkbox",
            validation: {},
            cols: 4,
            options: [{ label: "Unknown", value: "unknown" }],
          },
        ],
      },
      {
        name: "relative1UnknownSubGroup",
        condition: {
          field: "relative1Unknown",
          value: false,
        },
        fields: [
          {
            component: "TextInput",
            name: "relative1FirstMiddleName",
            label: "First and Middle Name",
            type: "text",
            maxLength: 20,
            validation: {
              required: "Please enter the first and middle names.",
              maxLength: 20,
            },
            cols: 4,
            placeholder: "",
          },
          {
            component: "TextInput",
            name: "relative1LastName",
            label: "Last Name (at Parent's Birth)",
            type: "text",
            maxLength: 16,
            validation: {
              required: "Please enter the last name.",
              maxLength: 16,
            },
            cols: 4,
            placeholder: "",
          },

          {
            component: "Date",
            name: "relative1BirthDate",
            label: "Date of Birth (Optional)",
            type: "date",
            maxElegibleDate: "today",
            cols: 4,
            validation: {},
            placeholder: "Please select a date.",
          },
          {
            component: "TextInput",
            name: "relative1BirthPlace",
            label: "Place of Birth (Optional)",
            type: "text",
            maxLength: 38,
            validation: {
              maxLength: 38,
            },
            cols: 4,
            placeholder: "City & State or City & Country",
          },
          {
            component: "Radio",
            name: "relative1Sex",
            label: "Gender",
            type: "radio",
            class: "radio--switch",
            tooltip:
              "You can select Male or Female. Please note that the U.S. government no longer accepts the 'X' (unspecified or other gender identity) option.",
            validation: {
              required: "Please enter the gender.",
            },
            cols: 4,
            options: gender,
          },
          {
            component: "Radio",
            name: "relative1Citizenship",
            label: "U.S. Citizen",
            class: "radio--inline",
            type: "radio",
            validation: {
              required: "Please enter the citizenship.",
            },
            cols: 4,
            options: getYesNoOptions({}),
          },
        ],
      },
      {
        name: "relative2Status",
        title: "Parent 2 details",
        fields: [
          {
            component: "Check",
            name: "relative2Unknown",
            label: "Unknown",
            type: "checkbox",
            validation: {},
            cols: 4,
            options: [{ label: "Unknown", value: "unknown" }],
          },
        ],
      },
      {
        name: "relative2UnknownSubGroup",
        condition: {
          field: "relative2Unknown",
          value: false,
        },
        fields: [
          {
            component: "TextInput",
            name: "relative2FirstMiddleName",
            label: "First and Middle Name",
            type: "text",
            maxLength: 20,
            validation: {
              required: "Please enter the first and middle names.",
              maxLength: 20,
            },
            cols: 4,
            placeholder: "",
          },
          {
            component: "TextInput",
            name: "relative2LastName",
            label: "Last Name (at Parent's Birth)",
            type: "text",
            maxLength: 16,
            validation: {
              required: "Please enter the last name.",
              maxLength: 16,
            },
            cols: 4,
            placeholder: "",
          },

          {
            component: "Date",
            name: "relative2BirthDate",
            label: "Date of Birth (Optional)",
            type: "date",
            maxElegibleDate: "today",
            validation: {},
            cols: 4,
            placeholder: "Please select a date.",
          },
          {
            component: "TextInput",
            name: "relative2BirthPlace",
            label: "Place of Birth (Optional)",
            type: "text",
            maxLength: 38,
            validation: {
              maxLength: 38,
            },
            cols: 4,
            placeholder: "City & State or City & Country",
          },
          {
            component: "Radio",
            name: "relative2Sex",
            label: "Gender",
            type: "radio",
            class: "radio--switch",
            tooltip:
              "You can select Male or Female. Please note that the U.S. government no longer accepts the 'X' (unspecified or other gender identity) option.",
            validation: {
              required: "Please enter the gender.",
            },
            cols: 4,
            options: gender,
          },
          {
            component: "Radio",
            name: "relative2Citizenship",
            label: "U.S. Citizen",
            class: "radio--inline",
            type: "radio",
            validation: {
              required: "Please enter the citizenship.",
            },
            cols: 4,
            options: getYesNoOptions({}),
          },
        ],
      },
      {
        name: "relativeMarriedStatus",
        fields: [
          {
            component: "Radio",
            name: "married",
            label: "Have you ever been married?",
            class: "radio--inline",
            type: "radio",
            validation: {
              required: "Please select an option.",
            },
            options: getYesNoOptions({}),
            placeholder: "",
          },
        ],
      },
      {
        name: "relative2SpouseOfApplicant",
        condition: {
          field: "married",
          value: "yes",
        },
        fields: [
          {
            component: "TextInput",
            name: "spouseFullName",
            label: "Your spouse's or former spouse's full name",
            type: "text",
            maxLength: 19,
            validation: {
              required: "Please enter the full name.",
              maxLength: 19,
            },
            placeholder: "",
          },
          {
            component: "Date",
            name: "spouseBirthDate",
            label: "Date of Birth",
            type: "date",
            maxElegibleDate: "today",
            validation: {
              required: "Please select a date.",
            },
            cols: 4,
            placeholder: "Please select a date.",
          },
          {
            component: "TextInput",
            name: "spouseBirthPlace",
            label: "Place of Birth",
            type: "text",
            maxLength: 25,
            validation: {
              required: "Please enter the city.",
              maxLength: 25,
            },
            cols: 4,
            placeholder: "City & State or City & Country",
          },
          {
            component: "Radio",
            name: "spouseCitizenship",
            label: "U.S. Citizen",
            class: "radio--inline",
            type: "radio",
            validation: {
              required: "Please enter the citizenship.",
            },
            cols: 4,
            options: getYesNoOptions({}),
            placeholder: "",
          },
          {
            component: "Date",
            name: "recentMarriageDate",
            label: "Date of Most Recent Marriage",
            type: "date",
            maxElegibleDate: "today",
            validation: {
              required: "Please select a date.",
            },
            cols: 4,
            placeholder: "Please select a date.",
          },
          {
            component: "Radio",
            name: "widowedDivorced",
            label: "Have you ever been widowed or divorced?",
            class: "radio--inline",
            type: "radio",
            validation: {
              required: "Please select an option.",
            },
            options: getYesNoOptions({}),
            placeholder: "",
          },
        ],
        subGroups: [
          {
            title: "",
            name: "relative2Widow",
            condition: {
              field: "widowedDivorced",
              value: "yes",
            },
            cols: 4,
            fields: [
              {
                component: "Date",
                name: "widowedDivorcedDate",
                label: "Date",
                type: "date",
                maxElegibleDate: "today",
                validation: {
                  required: "Please select a date.",
                },
                cols: 4,
                placeholder: "Please select a date.",
              },
            ],
          },
        ],
      },
    ],
  };
};
